import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const WellnessStats = () => {
  const [counters, setCounters] = useState({
    centers: 0,
    programs: 0,
    guests: 0,
    practitioners: 0
  });

  const finalStats = {
    centers: 1847,
    programs: 3284,
    guests: 125847,
    practitioners: 892
  };

  const stats = [
    {
      key: 'centers',
      value: counters.centers,
      finalValue: finalStats.centers,
      label: 'Wellness Centers',
      subtext: 'Verified & Certified',
      icon: 'Building2',
      color: 'from-primary to-secondary' // Updated from blue-500 to blue-600
    },
    {
      key: 'programs',
      value: counters.programs,
      finalValue: finalStats.programs,
      label: 'Wellness Programs',
      subtext: 'Available Experiences',
      icon: 'Sparkles',
      color: 'from-accent to-primary' // Updated from teal-500 to teal-600
    },
    {
      key: 'guests',
      value: counters.guests,
      finalValue: finalStats.guests,
      label: 'Satisfied Guests',
      subtext: 'Life Transformations',
      icon: 'Users',
      color: 'from-secondary to-accent' // Updated from purple-500 to purple-600
    },
    {
      key: 'practitioners',
      value: counters.practitioners,
      finalValue: finalStats.practitioners,
      label: 'Certified Practitioners',
      subtext: 'Expert Healers',
      icon: 'Award',
      color: 'from-primary to-accent' // Updated from emerald-500 to emerald-600
    }
  ];

  useEffect(() => {
    const duration = 2000; // 2 seconds
    const interval = 50; // Update every 50ms
    const steps = duration / interval;

    const incrementers = {
      centers: finalStats.centers / steps,
      programs: finalStats.programs / steps,
      guests: finalStats.guests / steps,
      practitioners: finalStats.practitioners / steps
    };

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;

      setCounters(prev => ({
        centers: Math.min(Math.floor(incrementers.centers * currentStep), finalStats.centers),
        programs: Math.min(Math.floor(incrementers.programs * currentStep), finalStats.programs),
        guests: Math.min(Math.floor(incrementers.guests * currentStep), finalStats.guests),
        practitioners: Math.min(Math.floor(incrementers.practitioners * currentStep), finalStats.practitioners)
      }));

      if (currentStep >= steps) {
        clearInterval(timer);
        // Ensure final values are exact
        setCounters(finalStats);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num.toLocaleString();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const statVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="mb-12"
      >
        <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4 font-heading">
          Transforming Lives Through Wellness
        </h2>
        <p className="text-lg text-text-secondary max-w-2xl mx-auto">
          Join a global community dedicated to holistic healing and energy enhancement
        </p>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-8"
      >
        {stats.map((stat) => (
          <motion.div
            key={stat.key}
            variants={statVariants}
            className="relative group"
          >
            <div className="bg-white rounded-2xl p-8 shadow-elevation-2 hover:shadow-elevation-3
                           transition-all duration-300 border border-border">
              {/* Icon */}
              <div className="mb-6">
                <div className={`w-16 h-16 mx-auto rounded-xl bg-gradient-to-br ${stat.color}
                                 flex items-center justify-center shadow-elevation-2`}>
                  <Icon name={stat.icon} size={28} className="text-white" />
                </div>
              </div>

              {/* Counter */}
              <div className="mb-4">
                <div className="text-3xl lg:text-4xl font-bold text-text-primary mb-2 font-heading">
                  {formatNumber(stat.value)}+
                </div>
                <div className="text-lg font-semibold text-text-primary">
                  {stat.label}
                </div>
                <div className="text-sm text-text-secondary mt-1">
                  {stat.subtext}
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="w-full bg-background-medium rounded-full h-1"> {/* Updated from bg-gray-100 */}
                <motion.div
                  className={`h-1 rounded-full bg-gradient-to-r ${stat.color}`}
                  initial={{ width: '0%' }}
                  whileInView={{
                    width: `${(stat.value / stat.finalValue) * 100}%`
                  }}
                  transition={{ duration: 2, ease: "easeOut" }}
                  viewport={{ once: true }}
                />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        viewport={{ once: true }}
        className="mt-12"
      >
        <div className="bg-white rounded-2xl p-8 shadow-elevation-2 border border-border
                         max-w-2xl mx-auto">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-primary to-accent rounded-full {/* Updated from blue-500 to teal-500 */}
                            flex items-center justify-center">
              <Icon name="Heart" size={24} className="text-white" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-text-primary mb-2 font-heading">
            Join Our Wellness Community
          </h3>
          <p className="text-text-secondary">
            Connect with like-minded individuals on their healing journey and discover
            transformative wellness experiences worldwide.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default WellnessStats;
